/**
 * Inline stroke SVG icons for the marketplace surface.
 *
 * Centralized so card, detail-view, rating, and report all share the same
 * 1.6px-stroke / 24-viewBox / currentColor glyphs. No emoji glyphs anywhere.
 */
import type { ReactElement } from 'react';

interface IconProps {
  size?: number;
  className?: string;
}

const STROKE = 1.6;

function Svg({
  size = 16,
  className,
  children,
  fill = 'none',
}: IconProps & { children: ReactElement | ReactElement[]; fill?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke="currentColor"
      strokeWidth={STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {children}
    </svg>
  );
}

export function StepIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M9 9h6v6H9z" />
    </Svg>
  );
}

export function WorkflowIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="12" cy="18" r="2.5" />
      <path d="M8 7.5 11 16M16 7.5l-3 8.5" />
    </Svg>
  );
}

export function AutomationIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </Svg>
  );
}

export function DocIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M7 3.5h7l3 3V20a1.5 1.5 0 0 1-1.5 1.5h-8A1.5 1.5 0 0 1 6 20V5A1.5 1.5 0 0 1 7.5 3.5Z" />
      <path d="M14 3.5V7h3.5M9 11h6M9 15h6M9 18h4" />
    </Svg>
  );
}

export function AppIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <rect x="3.5" y="4" width="17" height="14.5" rx="2.5" />
      <path d="M3.5 8h17M7 12h3.5M7 15h6M15.5 12h1.5M15.5 15h1.5M8 21h8" />
    </Svg>
  );
}

export function AgentIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 21v-2.5A5.5 5.5 0 0 1 10.5 13h3A5.5 5.5 0 0 1 19 18.5V21" />
      <path d="M9.5 8h.01M14.5 8h.01" />
    </Svg>
  );
}

export function DownloadIcon(p: IconProps): ReactElement {
  return (
    <Svg size={p.size ?? 12} className={p.className}>
      <path d="M12 4v12M6 12l6 6 6-6M5 20h14" />
    </Svg>
  );
}

export function PlayIcon(p: IconProps): ReactElement {
  return (
    <Svg size={p.size ?? 11} className={p.className} fill="currentColor">
      <path d="M7 4.5v15l13-7.5z" />
    </Svg>
  );
}

export function CheckIcon(p: IconProps): ReactElement {
  return (
    <Svg size={p.size ?? 14} className={p.className}>
      <path d="M5 12.5l4.5 4.5L19 7" />
    </Svg>
  );
}

export function StarIcon({
  size = 12,
  filled = true,
  className,
}: IconProps & { filled?: boolean }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M12 2.5l2.9 6.4 6.6.7-5 4.6 1.4 6.7L12 17.5l-5.9 3.4 1.4-6.7-5-4.6 6.6-.7L12 2.5z" />
    </svg>
  );
}

export function SearchIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </Svg>
  );
}

export function SortIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M3 6h13M3 12h9M3 18h5M17 4v16M17 20l4-4M17 20l-4-4" />
    </Svg>
  );
}

export function ChevronLeftIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M15 6l-6 6 6 6" />
    </Svg>
  );
}

export function ChevronRightIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M9 6l6 6-6 6" />
    </Svg>
  );
}

export function ChevronDownIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M6 9l6 6 6-6" />
    </Svg>
  );
}

export function ArrowLeftIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M19 12H5M12 5l-7 7 7 7" />
    </Svg>
  );
}

export function FlagIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M5 3v18M5 16h12l-3-4 3-4H5" />
    </Svg>
  );
}

export function EmptyBoxIcon(p: IconProps): ReactElement {
  return (
    <Svg size={p.size ?? 56} className={p.className}>
      <path d="M3 8l9-5 9 5v8l-9 5-9-5V8z" />
      <path d="M3 8l9 5 9-5M12 13v8" opacity={0.5} />
    </Svg>
  );
}

export function AlertIcon(p: IconProps): ReactElement {
  return (
    <Svg size={p.size ?? 56} className={p.className}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 7v6M12 17v.01" />
    </Svg>
  );
}

export function ShieldIcon(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" />
    </Svg>
  );
}
